import { useContext, useEffect, useState } from 'react';
import UserContext from '../Contexts/UserContext';
import { default as axios } from '../axiosConfig';
import {
	API_GetActiveForms,
	API_GetArchivedForms,
	API_GetCandidateIdFromEmail,
	API_GetUserActiveForms,
	API_GetUserArchiveForms,
	API_UpdateFormStatus,
	API_UploadFormToDrive,
	API_ReUploadFormToDrive,
	API_GetMissingRTRForms,
	API_UploadRTRForm
} from '../Assets/ApiUrlContainer';
import InLineLoadingSpinner from './LoadingSpinner';
import { Pagination } from './Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const FormManagementPage = () => {
	const { user: loggedInUser } = useContext(UserContext);
	const [archivedForms, setArchivedForms] = useState([]);
	const [activeForms, setActiveForms] = useState([]);

	const [unModifiedArchivedForms, setUnModifiedArchivedForms] = useState([]);
	const [unModifiedActiveForms, setUnModifiedActiveForms] = useState([]);

	const [selectedFiles, setSelectedFiles] = useState({});
	const [filesUploading, setFilesUploading] = useState([]);

	const [activeAdminListDisplayStartingIndex, setActiveAdminListDisplayStartingIndex] = useState(0);
	const [activeAdminListDisplayLimit, setActiveAdminListDisplayLimit] = useState(10);
	const [activeListDisplayStartingIndex, setActiveListDisplayStartingIndex] = useState(0);
	const [activeListDisplayLimit, setActiveListDisplayLimit] = useState(10);
	const [archiveListDisplayStartingIndex, setArchiveListDisplayStartingIndex] = useState(0);
	const [archiveListDisplayLimit, setArchiveListDisplayLimit] = useState(10);

	const [searchArchiveText, setSearchArchiveText] = useState('');
	const [triggerArchiveReset, setTriggerArchiveReset] = useState(false);

	const [searchActiveText, setSearchActiveText] = useState('');
	const [triggerActiveReset, setTriggerActiveReset] = useState(false);

	const FILE_MAX_CHAR_DISPLAY_LIMIT = 25;

	const [missingRTRForms, setMissingRTRForms] = useState([]);
	const [selectedRTRFiles, setSelectedRTRFiles] = useState({});
	const [isUploadingRTR, setIsUploadingRTR] = useState(false);

	const [searchRTRText, setSearchRTRText] = useState('');
	const [triggerRtrReset, setTriggerRtrReset] = useState(false);
	const [filteredRTRForms, setFilteredRTRForms] = useState([]);
	const [RTRListDisplayStartingIndex, setRTRListDisplayStartingIndex] = useState(0);
	const [RTRListDisplayLimit, setRTRListDisplayLimit] = useState(10);

	useEffect(() => {
		fetchAllFormDetails();
		fetchMissingRTRForms();
	}, []);

	useEffect(() => {
		setFilteredRTRForms(missingRTRForms);
	}, [missingRTRForms]);


	const fetchAllFormDetails = async () => {
		let candidateId = 0;

		if (!loggedInUser.IsGoogleUser) {
			candidateId = await fetchCandidateId();
			console.log('Setting candidateId:', candidateId);
		}

		fetchActiveForms(candidateId);
		fetchArchivedForms(candidateId);
	};

	const fetchMissingRTRForms = async () => {
		try {
			const response = await axios.get(API_GetMissingRTRForms, { withCredentials: true });
			setMissingRTRForms(response.data);
		} catch (error) {
			console.error("Error fetching missing Right to Represent forms:", error);
		}
	};


	const fetchCandidateId = async () => {
		try {
			let response = await axios.get(`${API_GetCandidateIdFromEmail}`, {
				params: { Email: loggedInUser.Email },
			});

			console.log('Fetched candidateID from API:', response.data); // Debug log

			return response.data.Id; // Ensure we return the actual ID
		} catch (error) {
			console.error('Error fetching candidateID:', error);
			return null;
		}
	};

	const fetchActiveForms = async (candidateId) => {
		try {
			let response = loggedInUser.IsAdmin
				? await axios.get(API_GetActiveForms, { withCredentials: true })
				: await axios.get(`${API_GetUserActiveForms}/${candidateId}`, { withCredentials: true });

			console.log('All Forms Received from Backend:', response.data);

			// Ensure rejected forms are included
			const activeFormsFiltered = response.data.filter((form) => form.Status !== 'approved');

			// Debugging: Check if FormId exists
			activeFormsFiltered.forEach((form) => {
				if (!form.FormId) {
					console.warn(`Missing FormId for form:`, form);
				}
			});

			setActiveForms(activeFormsFiltered);
			setUnModifiedActiveForms(activeFormsFiltered)
		} catch (error) {
			console.error('Error fetching active forms:', error);
		}
	};

	const fetchArchivedForms = async (candidateId) => {
		let response = loggedInUser.IsAdmin
			? await axios.get(API_GetArchivedForms, { withCredentials: true })
			: await axios.get(`${API_GetUserArchiveForms}/${candidateId}`, { withCredentials: true });

		setArchivedForms(response.data);
		setUnModifiedArchivedForms(response.data);
	};

	// For non-admin users: file upload handling
	const handleFileChange = (event, formId) => {
		const file = event.target.files[0];

		if (file) {
			if (file.type !== 'application/pdf') {
				alert('Only PDF files are allowed.');
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				alert('File size exceeds 5MB limit.');
				return;
			}

			setSelectedFiles((prev) => ({ ...prev, [formId]: file }));
		}
	};

	const handleRTRFileChange = (event, assignedFormId) => {
		setSelectedRTRFiles((prev) => ({
			...prev,
			[assignedFormId]: event.target.files[0],
		}));
	};


	const handleUploadForm = async (form) => {
		setFilesUploading([...filesUploading, form.AssignedFormId]);

		let candidateId = await fetchCandidateId(); // Ensure we fetch candidate ID before submission

		console.log('Sending data to backend:', {
			candidateID: candidateId,
			candidateName: `${loggedInUser.FirstName} ${loggedInUser.LastName}`,
			companyID: form.CompanyId,
			companyName: form.CompanyName,
			formType: form.FormType,
			formID: form.FormId,
			assignedFormID: form.AssignedFormId,
		});

		if (!candidateId) {
			alert('Candidate ID is missing. Please try again.');
			return;
		}

		if (!selectedFiles[form.AssignedFormId]) {
			alert('Please select a file before uploading.');
			return;
		}

		const formData = new FormData();
		formData.append('formFile', selectedFiles[form.AssignedFormId]);
		formData.append('candidateId', candidateId);
		formData.append('candidateName', `${loggedInUser.FirstName} ${loggedInUser.LastName}`);
		formData.append('companyId', form.CompanyId);
		formData.append('companyName', form.CompanyName);
		formData.append('formType', form.FormType);
		formData.append('formId', form.FormId);
		formData.append('assignedFormId', form.AssignedFormId);

		try {
			await axios.post(API_UploadFormToDrive, formData, {
				withCredentials: true,
				headers: { 'Content-Type': 'multipart/form-data' },
			});

			// alert('Form uploaded successfully.');
			fetchAllFormDetails();
		} catch (error) {
			console.error('Error uploading form:', error);
			alert('Failed to upload form. Please try again.');
		} finally {
			let indexAssignedId = filesUploading.indexOf(form.AssignedFormId);
			setFilesUploading([...filesUploading.slice(0, indexAssignedId), ...filesUploading.slice(indexAssignedId + 1, filesUploading.length)]);
		}
	};

	const handleRTRUpload = async (form) => {
		if (!selectedRTRFiles[form.AssignedFormId]) {
			alert("Please select a file before uploading.");
			return;
		}

		setIsUploadingRTR(true);

		const formData = new FormData();
		formData.append("formFile", selectedRTRFiles[form.AssignedFormId]);
		formData.append("candidateId", form.CandidateId);
		formData.append("candidateName", form.CandidateName);
		formData.append("companyId", "0"); // No company for RTR
		formData.append("companyName", "TGS");
		formData.append("assignedFormId", form.AssignedFormId);

		try {
			await axios.post(API_UploadRTRForm, formData, {
				withCredentials: true,
				headers: { "Content-Type": "multipart/form-data" },
			});

			alert("Right to Represent form uploaded successfully.");
			await fetchAllFormDetails();
			await fetchMissingRTRForms();

		} catch (error) {
			console.error("Error uploading RTR form:", error);
			alert("Failed to upload form. Please try again.");
		} finally {
			setIsUploadingRTR(false);
		}
	};

	const handleResubmitForm = async (form) => {
		setFilesUploading([...filesUploading, form.AssignedFormId]);
		console.log('Checking form object before resubmitting:', form);

		if (!form.AssignedFormId) {
			console.error('AssignedFormId is missing! Cannot resubmit.');
			alert('Assigned Form ID is missing. Please contact support.');
			return;
		}

		// Fetch candidateID explicitly before sending the request
		let candidateId = await fetchCandidateId();

		if (!candidateId) {
			console.error('Candidate ID is missing. Cannot resubmit.');
			alert('Candidate ID is missing. Please contact support.');
			return;
		}

		try {
			await axios.post(API_ReUploadFormToDrive, { candidateId: candidateId, assignedFormId: form.AssignedFormId, companyId: form.CompanyId }, {
				withCredentials: true,
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			});

			alert('Form resubmission enabled. You can now upload the form again.');
			fetchAllFormDetails();
		} catch (error) {
			console.error('Error resubmitting form:', error);
			alert('Failed to resubmit form. Please try again.');
		} finally {
			let indexAssignedId = filesUploading.indexOf(form.AssignedFormId);
			setFilesUploading([...filesUploading.slice(0, indexAssignedId), ...filesUploading.slice(indexAssignedId + 1, filesUploading.length)]);
		}
	};

	// Admin functions: approve or reject forms
	const handleFormAccept = async (formId) => {
		try {
			await axios.post(API_UpdateFormStatus, { assignedFormId: formId, status: 'approved' }, {
				withCredentials: true,
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			});

			fetchAllFormDetails();
		} catch (error) {
			console.error('Error approving form:', error);
			alert('Failed to approve form. Please try again.');
		}
	};

	const handleFormReject = async (formId) => {
		try {
			await axios.post(API_UpdateFormStatus, { assignedFormId: formId, status: 'rejected' }, {
				withCredentials: true,
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
			});

			fetchAllFormDetails();
		} catch (error) {
			console.error('Error rejecting form:', error);
			alert('Failed to reject form. Please try again.');
		}
	};

	const yyyymmddDateString = (dateToConvert) => {
		let date = new Date(dateToConvert);
		let dateString = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + (date.getDate() + 1)).slice(-2)}`;
		return dateString;
	};

	const getFileUploadFileName = (formAssignedId) => {
		let text = selectedFiles[formAssignedId]?.name ? selectedFiles[formAssignedId].name : "Choose File";
		if (text !== 'Choose File') {
			let needsCondensing = text.length > FILE_MAX_CHAR_DISPLAY_LIMIT;
			if (needsCondensing) {
				text = `${text.slice(0, FILE_MAX_CHAR_DISPLAY_LIMIT)}...`;
			}
		}

		return text;
	}

	const isFileSelected = (formAssignedId) => {
		return selectedFiles[formAssignedId] ? true : false;
	}

	const getIsFileUploading = (formAssignedId) => filesUploading.includes(formAssignedId);

	const handlePageChange = (newPositionIndex, isActiveList = false) => {
		if (!isActiveList) {
			setArchiveListDisplayStartingIndex(newPositionIndex);
			return;
		}

		if (loggedInUser.IsAdmin) {
			setActiveAdminListDisplayStartingIndex(newPositionIndex);
			return;
		}

		setActiveListDisplayStartingIndex(newPositionIndex);
	}

	const handlePageListLimitChange = (newListDisplayLimit, isActiveList) => {
		if (!isActiveList) {
			setArchiveListDisplayLimit(newListDisplayLimit);
			return;
		}

		if (loggedInUser.IsAdmin) {
			setActiveAdminListDisplayLimit(newListDisplayLimit);
			return;
		}

		setActiveListDisplayLimit(newListDisplayLimit);
	}

	const handleRTRPageChange = (newPositionIndex) => {
		setRTRListDisplayStartingIndex(newPositionIndex);
	};

	const handleRTRPageListLimitChange = (newListDisplayLimit) => {
		setRTRListDisplayLimit(newListDisplayLimit);
	};


	const compareString = (stringCmp, searchText) => {
		return stringCmp.toLowerCase().includes(searchText.toLowerCase())
	}

	const handleSearchArchiveFilter = (event) => {
		let searchText = event.target.value;
		setSearchArchiveText(searchText);
		setTriggerArchiveReset(true);
		setArchivedForms(
			unModifiedArchivedForms.filter(
				archivedForm =>
					compareString(loggedInUser.IsAdmin ? archivedForm.CandidateName : "", searchText) ||
					compareString(archivedForm.CompanyName, searchText) ||
					compareString(archivedForm.FormType, searchText)
			)
		)
	}

	const handleSearchActiveFilter = (event) => {
		let searchText = event.target.value;
		setSearchActiveText(searchText);
		setTriggerActiveReset(true);
		setActiveForms(
			unModifiedActiveForms.filter(
				activeForm =>
					compareString(loggedInUser.IsAdmin ? activeForm.CandidateName : "", searchText) ||
					compareString(activeForm.CompanyName, searchText) ||
					compareString(activeForm.FormType, searchText)
			)
		)
	}

	const handleSearchRTRFilter = (event) => {
		let searchText = event.target.value;
		setSearchRTRText(searchText);
		setTriggerRtrReset(true);

		// Debugging: Check if missingRTRForms is populated
		console.log("Search Input:", searchText);
		console.log("Missing RTR Forms:", missingRTRForms);

		// Filter and update the state
		const filteredForms = missingRTRForms.filter((form) =>
			form.CandidateName.toLowerCase().includes(searchText.toLowerCase())
		);

		console.log("Filtered Forms:", filteredForms); // Debugging output
		setFilteredRTRForms(filteredForms);
	};

	return (
		<>
			<div className='formManagementContainer'>
				<div className='tgs-content'>
					<h4 className='formPageHeading'>Form Management</h4>
				</div>

				{/* Non-admin view updated to use same table structure and styling as admin */}
				{!loggedInUser.IsAdmin && (
					<>
						<div className='tgs-content'>
							<div className='formsContentHeading'>Active Forms</div>
							<div className='searchBar'>
								<span>
									<FontAwesomeIcon icon={faSearch} />
								</span>
								<input
									type='text'
									value={searchActiveText}
									onInput={handleSearchActiveFilter}
									placeholder='Search'
								/>
							</div>

							<table className='formTable adminActiveForms'>
								<thead>
									<tr>
										<th>Company Name</th>
										<th>Form Type</th>
										<th>Due Date</th>
										<th>PDF URL</th>
										<th>Status</th>
										<th>Upload</th>
									</tr>
								</thead>
								<tbody>
									{activeForms
										.slice(activeListDisplayStartingIndex, activeListDisplayLimit)
										.map((form) => (
											<tr key={form.AssignedFormId}>
												<td>{form.CompanyName}</td>
												<td>
													<a href={form.FormTemplateUrl} target='_blank' rel='noopener noreferrer'>
														{form.FormType}
													</a>
												</td>
												<td>{yyyymmddDateString(form.DueDate)}</td>
												<td>
													{form.PdfUrl ? (
														<a href={form.PdfUrl} target='_blank' rel='noopener noreferrer'>
															View Document
														</a>
													) : (
														'No File Submitted'
													)}
												</td>
												<td className={`status ${form.Status?.toLowerCase() || 'pending'}`}>
													{form.Status === 'rejected' ? (
														<span style={{ color: 'red', fontWeight: 'bold' }}>Rejected</span>
													) : (
														form.Status || 'Pending'
													)}
												</td>

												<td className='uploadSection'>
													{(form.Status === 'pending' || form.Status === 'rejected') && (
														<label className={`customFileUpload ${getIsFileUploading(form.AssignedFormId) ? " disabled" : ""}`}>
															<input
																id={`file-upload-${form.AssignedFormId}`}
																style={{ display: 'none' }}
																type='file'
																accept='application/pdf'
																onChange={(e) => handleFileChange(e, form.AssignedFormId)}
																className='fileInput'
																disabled={getIsFileUploading(form.AssignedFormId)}
															/>
															{getFileUploadFileName(form.AssignedFormId)}
														</label>
													)}

													{getIsFileUploading(form.AssignedFormId) && <InLineLoadingSpinner />}

													{(form.Status === 'pending' || form.Status === 'rejected') && isFileSelected(form.AssignedFormId) && !getIsFileUploading(form.AssignedFormId) && (
														<button className='submitBtn' onClick={() => handleUploadForm(form)}>Submit</button>
													)}

													{form.Status === 'submitted' && (
														<div className="submittedFile">Awaiting Approval</div>
													)}
												</td>
											</tr>
										))}
								</tbody>
							</table>

							<Pagination
								totalNumItems={activeForms.length}
								onPageChangeNewPosition={(newPos) => handlePageChange(newPos, true)}
								onRowsChangeNewDisplayLimit={(newLimit) => handlePageListLimitChange(newLimit, true)}
								triggerPageReset={triggerActiveReset}
								onTriggerReset={setTriggerActiveReset}
							/>
						</div>
					</>
				)}

				{/* Admin view: Right To Represent Upload, Active Forms with Approve/Reject and Archived Forms */}
				{loggedInUser.IsAdmin && (
					<>
						<div className='tgs-content'>
							<div className='formsContentHeading'>Upload Right To Represent</div>
							{/* Search Bar */}
							<div className='searchBar'>
								<span>
									<FontAwesomeIcon icon={faSearch} />
								</span>
								<input
									type='text'
									value={searchRTRText}
									onInput={handleSearchRTRFilter}
									placeholder='Search Candidate Name'
								/>
							</div>
							<div className='formsUploadContent'>
								<table className='formTable'>
									<thead>
										<tr>
											<th>Candidate Name</th>
											<th>Status</th>
											<th>Upload</th>
											{/* <th>File</th> */}
										</tr>
									</thead>
									<tbody>
										{filteredRTRForms
											.slice(RTRListDisplayStartingIndex, RTRListDisplayLimit)
											.map((form) => (
												<tr key={form.AssignedFormId}>
													<td>{form.CandidateName}</td>
													<td>{form.SubmittedPdfUrl ? 'Submitted' : 'Missing'}</td>
													<td className='uploadSection'>
														{form.SubmittedPdfUrl ? (
															"Uploaded"
														) : (
															<>
																<label className={`customFileUpload ${isUploadingRTR ? " disabled" : ""}`}>
																	<input
																		type='file'
																		accept='application/pdf'
																		onChange={(e) => handleRTRFileChange(e, form.AssignedFormId)}
																		style={{ display: 'none' }}
																		disabled={isUploadingRTR}
																	/>
																	{selectedRTRFiles[form.AssignedFormId]
																		? selectedRTRFiles[form.AssignedFormId].name
																		: "Choose File"}
																</label>

																{selectedRTRFiles[form.AssignedFormId] && !isUploadingRTR && (
																	<button className='submitBtn' onClick={() => handleRTRUpload(form)}>Submit</button>
																)}

																{isUploadingRTR && <InLineLoadingSpinner />}
															</>
														)}
													</td>
													{/* <td>
												{form.SubmittedPdfUrl ? (
													<a href={form.SubmittedPdfUrl} target="_blank" rel="noopener noreferrer">
														View
													</a>
													) : (
														"No File"
													)}
											</td> */}
												</tr>
											))}
									</tbody>
								</table>
								{/* Pagination */}
								<Pagination
									totalNumItems={filteredRTRForms.length}
									onPageChangeNewPosition={(newPos) => handleRTRPageChange(newPos)}
									onRowsChangeNewDisplayLimit={(newLimit) => handleRTRPageListLimitChange(newLimit)}
									triggerPageReset={triggerRtrReset}
									onTriggerReset={setTriggerRtrReset}
								/>
							</div>
						</div>

						<div className='tgs-content'>
							<div className='formsContentHeading'>Active Forms</div>
							<div className='searchBar'>
								<span>
									<FontAwesomeIcon icon={faSearch} />
								</span>
								<input
									type='text'
									value={searchActiveText}
									onInput={handleSearchActiveFilter}
									placeholder='Search'
								/>
							</div>

							<table className='formTable adminActiveForms'>
								<thead>
									<tr>
										<th>Candidate Name</th>
										<th>Company Name</th>
										<th>Form Type</th>
										<th>PDF URL</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{activeForms
										.slice(activeAdminListDisplayStartingIndex, activeAdminListDisplayLimit)
										.map((form) => (
											<tr key={form.AssignedFormId}>
												<td>{form.CandidateName}</td>
												<td>{form.CompanyName}</td>
												<td>{form.FormType}</td>
												<td>
													{form.PdfUrl ? (
														<a href={form.PdfUrl} target='_blank' rel='noopener noreferrer'>
															View Document
														</a>
													) : (
														'No File Submitted'
													)}
												</td>
												<td>
													{form.PdfUrl && (
														<>
															<button
																className='acceptBtn'
																onClick={() => handleFormAccept(form.AssignedFormId)}
															>
																Approve
															</button>
															<button
																className='rejectBtn'
																onClick={() => handleFormReject(form.AssignedFormId)}
															>
																Reject
															</button>
														</>
													)}
												</td>
											</tr>
										))}
								</tbody>
							</table>

							<Pagination
								totalNumItems={activeForms.length}
								onPageChangeNewPosition={(newPos) => handlePageChange(newPos, true)}
								onRowsChangeNewDisplayLimit={(newLimit) => handlePageListLimitChange(newLimit, true)}
								triggerPageReset={triggerActiveReset}
								onTriggerReset={setTriggerActiveReset}
							/>
						</div>
					</>
				)}

				<div className='tgs-content'>
					<div className='formsContentHeading'>Archived Forms</div>
					<div className='searchBar'>
						<span>
							<FontAwesomeIcon icon={faSearch} />
						</span>
						<input
							type='text'
							value={searchArchiveText}
							onInput={handleSearchArchiveFilter}
							placeholder='Search'
						/>
					</div>

					<table className='formTable adminArchivedForms'>
						<thead>
							<tr>
								{loggedInUser.IsAdmin && <th>Candidate Name</th>}
								<th>Company Name</th>
								<th>Form Type</th>
								<th>PDF URL</th>
							</tr>
						</thead>
						<tbody>
							{archivedForms
								.slice(archiveListDisplayStartingIndex, archiveListDisplayLimit)
								.map((form) => (
									<tr key={form.AssignedFormId}>
										{loggedInUser.IsAdmin && <td>{form.CandidateName}</td>}
										<td>{form.CompanyName}</td>
										<td>{form.FormType}</td>
										<td>
											<a href={form.PdfUrl} target='_blank' rel='noopener noreferrer'>
												View Document
											</a>
										</td>
									</tr>
								))}
						</tbody>
					</table>

					<Pagination
						totalNumItems={archivedForms.length}
						onPageChangeNewPosition={(newPos) => handlePageChange(newPos)}
						onRowsChangeNewDisplayLimit={(newLimit) => handlePageListLimitChange(newLimit)}
						triggerPageReset={triggerArchiveReset}
						onTriggerReset={setTriggerArchiveReset}
					/>
				</div>
			</div>
		</>
	);
};

export default FormManagementPage;
